import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import {
  iconNameFromKind,
  isPickedLibraryIcon,
  LOCATION_KIND_PROPERTY,
} from '~/client/src/shared/resources/domain/locationKind'

import { IResourceDefinition, IResourceFieldDefinition } from './types'

// ID, Icon and Name lead the table and stay frozen; Type and Parent follow, and the five
// together are the only columns visible by default. Everything else a project adds as a
// resource property is available through Display options.
const LOCATION_FROZEN_COLUMNS = ['stxResourceId', 'iconName', 'name']
const LOCATION_DEFAULT_COLUMNS = [...LOCATION_FROZEN_COLUMNS, 'type', 'parent']

// Pre-selected on a new Location form. Name, Icon, Color and Parent are system fields the
// editor always includes, so only the kind is listed here.
const LOCATION_DEFAULT_FORM_PROPERTIES = ['type']

const LOCATION_FIELD_DEFINITIONS: Record<string, IResourceFieldDefinition> = {
  // The location kind. Backed by the LocationTypeHierarchy taxonomy the backend seeds
  // into every project's resource properties, so it behaves like Equipment's own `type`
  // column - a two-level hierarchy, groupable and filterable at either level.
  type: {
    dataType: 'text',
    card: { kind: 'text', visible: true },
    table: {
      sortable: true,
      groupable: true,
      filterable: true,
      searchable: true,
      labelKey: 'type',
      // Grouping by kind nests root above leaf, and iconNameFromKind answers for both -
      // a leaf by its own glyph, a root by the root's. Null for an option a project added
      // itself, which has no artwork; that header shows its label alone rather than
      // borrowing a glyph that would misname it.
      groupIconName: iconNameFromKind,
    },
  },
  // Location root fields (edited in the resource form) exposed to the table, the same
  // three Equipment exposes. iconName/color are display-only - glyph and swatch, not
  // sortable/groupable/filterable - while parent is a location reference that resolves to
  // its parent's name through the location rail rather than showing a raw ObjectId.
  iconName: {
    dataType: 'text',
    card: { visible: false },
    table: { column: true, cellType: 'icon', labelKey: 'icon' },
  },
  color: {
    dataType: 'text',
    card: { visible: false },
    table: { column: true, cellType: 'color', labelKey: 'color' },
  },
  parent: {
    dataType: 'location',
    // The LBS tag on a location's card - the place it sits under, resolved to a name by
    // the resource-link registry rather than shown as an id.
    card: { kind: 'text', visible: true },
    table: {
      sortable: true,
      groupable: true,
      filterable: true,
      // Matches the card's label: the column, filter, group-by and sort all read this.
      labelKey: 'lbsParent',
    },
  },
}

export const locationResourceDefinition: IResourceDefinition = {
  resourceTypeId: ResourceTypeId.LOCATIONS,
  fields: LOCATION_FIELD_DEFINITIONS,
  valueSources: {
    stxResourceId: { source: 'root' },
    // iconName/color/parent are top-level record props (toRecord puts them there, not in
    // root/data), so read them off the record directly. `parent` projects to its parentId
    // so the location rail can resolve the parent's name.
    iconName: {
      source: 'root',
      resolve: record => record.iconName ?? null,
    },
    color: {
      source: 'root',
      resolve: record => record.color ?? null,
    },
    parent: {
      source: 'root',
      resolve: record => record.parent?.parentId ?? null,
    },
  },
  table: {
    columnOrder: LOCATION_DEFAULT_COLUMNS,
    defaultVisibleKeys: LOCATION_DEFAULT_COLUMNS,
    defaultFrozenKeys: LOCATION_FROZEN_COLUMNS,
  },
  card: {
    titleKey: 'name',
    subtitleKey: 'stxResourceId',
    // Nothing: the icon carries the kind and the subtitle carries the code and the LBS,
    // so the card has no third row to fill.
    defaultDisplayPropertyKeys: [],
  },
  form: {
    defaultSelectedKeys: LOCATION_DEFAULT_FORM_PROPERTIES,
    // A location without a kind is not a location: it drives the glyph, the hierarchy and
    // every kind-keyed rule. Always shown and always required, like the system fields
    // above it, rather than a property a template can drop.
    mandatoryPropertyKeys: LOCATION_DEFAULT_FORM_PROPERTIES,
    defaultRequiredKeys: LOCATION_DEFAULT_FORM_PROPERTIES,
    // Parent comes from the location a location is drawn inside, so it is only worth
    // asking for in Maps - the same call Equipment makes.
    mapsOnlyRootFields: ['parent'],
    // Picking a kind picks the glyph that names it, so a location changed from a gate to
    // a stairwell stops being drawn as a gate. Not the reverse - see iconNameFromKind.
    // A library icon that was not one of the previous kind's own was picked for its own
    // sake, so it survives the change.
    deriveFieldUpdates: (fieldName, value, values, previousValues) => {
      if (
        fieldName !== LOCATION_KIND_PROPERTY ||
        isPickedLibraryIcon(
          values.iconName as string,
          previousValues?.[LOCATION_KIND_PROPERTY] as string,
        )
      ) {
        return null
      }

      const iconName = iconNameFromKind(value as string)

      return iconName ? { iconName } : null
    },
  },
  ui: {
    noneGroupingKey: 'none',
    defaultGroupingKey: null,
    pickerTreeGroupingKey: 'parent',
    pickerDefaultGroupingKey: 'type',
    pickerGroupings: [
      { key: 'parent', glyph: 'lbs' },
      { key: 'type', glyph: 'location' },
    ],
    defaultSortKey: 'name',
    defaultSortOrder: SortOrder.ASC,
  },
}
